import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Heart, ShieldCheck, Camera, X, Plus } from "@phosphor-icons/react";

const MEDIA_BASE = API.replace(/\/api$/, "");
export const mediaUrl = (u) => (u ? `${MEDIA_BASE}${u}` : "");

const GENDERS = [["woman", "Woman"], ["man", "Man"], ["nonbinary", "Non-binary"], ["other", "Other"]];
const INTENTIONS = [["dating", "Dating"], ["serious", "Serious Relationship"], ["marriage", "Marriage"], ["friendship", "Friendship"]];

export function TagInput({ value, onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const add = () => {
    const v = text.trim().toLowerCase();
    if (v && !value.includes(v)) onChange([...value, v]);
    setText("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <input data-testid={`${testid}-input`} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder} className="flex-1 rounded-xl border border-border bg-card px-3 py-2 text-sm" />
        <button type="button" data-testid={`${testid}-add`} onClick={add} className="rounded-xl bg-secondary px-3"><Plus size={16} /></button>
      </div>
      {value.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {value.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2.5 py-1 text-xs font-medium capitalize text-rose-600 dark:text-rose-300">
              {t}<button type="button" onClick={() => onChange(value.filter((x) => x !== t))}><X size={11} weight="bold" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MatchOnboarding({ onDone }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    confirm_adult: false, accept_terms: false, display_name: "", dob: "", gender: "",
    seeking: [], intention: "dating", bio: "", city: "", region: "", country: "",
    languages: [], interests: [], photos: [],
    prefs: { age_min: 25, age_max: 45, verified_only: false },
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const [uploading, setUploading] = useState(false);

  const activate = useMutation({
    mutationFn: async () => (await api.post("/match/activate", {
      ...f, seeking: f.seeking, prefs: { ...f.prefs, seeking: f.seeking },
    })).data,
    onSuccess: () => { toast.success("Welcome to iYeneh Match! Start discovering."); qc.invalidateQueries({ queryKey: ["match-me"] }); onDone?.(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/match/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photos", [...f.photos, { url: data.url }]);
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const toggleSeeking = (g) => set("seeking", f.seeking.includes(g) ? f.seeking.filter((x) => x !== g) : [...f.seeking, g]);
  const canSubmit = f.confirm_adult && f.accept_terms && f.display_name && f.dob && f.gender && f.seeking.length > 0;

  return (
    <div className="mx-auto max-w-2xl" data-testid="match-onboarding">
      <div className="mb-6 text-center">
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-rose-500 text-white"><Heart size={30} weight="fill" /></div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Activate iYeneh Match</h1>
        <p className="mt-2 text-sm text-muted-foreground">A private, opt-in space — separate from your iYeneh profile. Adults 18+ only.</p>
      </div>

      <div className="space-y-6 rounded-3xl border border-border bg-card p-5 md:p-7">
        <label className="flex items-start gap-3 rounded-2xl bg-secondary/60 p-3.5" data-testid="onb-adult">
          <input type="checkbox" checked={f.confirm_adult} onChange={(e) => set("confirm_adult", e.target.checked)} className="mt-0.5 h-5 w-5 accent-rose-500" />
          <span className="text-sm">I confirm I am <strong>18 years or older</strong>.</span>
        </label>

        <Field label="Display name">
          <input data-testid="onb-name" value={f.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder="How you'll appear" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </Field>
        <Field label="Date of birth" hint="Only your age is shown publicly — never your birth date.">
          <input data-testid="onb-dob" type="date" value={f.dob} onChange={(e) => set("dob", e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </Field>

        <Field label="I am a">
          <div className="flex flex-wrap gap-2">
            {GENDERS.map(([v, l]) => (
              <button key={v} type="button" data-testid={`onb-gender-${v}`} onClick={() => set("gender", v)}
                className={`rounded-full border px-3.5 py-1.5 text-sm ${f.gender === v ? "border-rose-500 bg-rose-500 text-white" : "border-border"}`}>{l}</button>
            ))}
          </div>
        </Field>
        <Field label="Interested in">
          <div className="flex flex-wrap gap-2">
            {GENDERS.map(([v, l]) => (
              <button key={v} type="button" data-testid={`onb-seeking-${v}`} onClick={() => toggleSeeking(v)}
                className={`rounded-full border px-3.5 py-1.5 text-sm ${f.seeking.includes(v) ? "border-rose-500 bg-rose-500 text-white" : "border-border"}`}>{l}</button>
            ))}
          </div>
        </Field>
        <Field label="Looking for">
          <div className="flex flex-wrap gap-2">
            {INTENTIONS.map(([v, l]) => (
              <button key={v} type="button" data-testid={`onb-intention-${v}`} onClick={() => set("intention", v)}
                className={`rounded-full border px-3.5 py-1.5 text-sm ${f.intention === v ? "border-rose-500 bg-rose-500 text-white" : "border-border"}`}>{l}</button>
            ))}
          </div>
        </Field>

        <div className="grid gap-4 md:grid-cols-3">
          <Field label="City"><input data-testid="onb-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" /></Field>
          <Field label="Region/State"><input data-testid="onb-region" value={f.region} onChange={(e) => set("region", e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" /></Field>
          <Field label="Country"><input data-testid="onb-country" value={f.country} onChange={(e) => set("country", e.target.value)} placeholder="e.g. NG" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" /></Field>
        </div>

        <Field label="About me">
          <textarea data-testid="onb-bio" value={f.bio} onChange={(e) => set("bio", e.target.value)} rows={3} maxLength={600} placeholder="Share a little about yourself…" className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
        </Field>
        <Field label="Languages"><TagInput testid="onb-languages" value={f.languages} onChange={(v) => set("languages", v)} placeholder="Add a language + Enter" /></Field>
        <Field label="Interests"><TagInput testid="onb-interests" value={f.interests} onChange={(v) => set("interests", v)} placeholder="Add an interest + Enter" /></Field>

        <Field label={`Photos (${f.photos.length}/5)`} hint="A clear photo of you helps you match.">
          <div className="flex flex-wrap gap-2">
            {f.photos.map((p, i) => (
              <div key={i} className="relative h-20 w-20 overflow-hidden rounded-xl border border-border">
                <img src={mediaUrl(p.url)} alt="" className="h-full w-full object-cover" />
                <button type="button" onClick={() => set("photos", f.photos.filter((_, j) => j !== i))} className="absolute right-1 top-1 rounded-full bg-black/60 p-0.5 text-white"><X size={11} /></button>
              </div>
            ))}
            {f.photos.length < 5 && (
              <label data-testid="onb-photo-upload" className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border text-xs text-muted-foreground hover:border-rose-500">
                <Camera size={20} />{uploading ? "…" : "Add"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
              </label>
            )}
          </div>
        </Field>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label={`Age range: ${f.prefs.age_min}–${f.prefs.age_max}`}>
            <div className="flex items-center gap-2">
              <input data-testid="onb-age-min" type="number" min={18} max={99} value={f.prefs.age_min} onChange={(e) => set("prefs", { ...f.prefs, age_min: +e.target.value })} className="w-20 rounded-xl border border-border bg-background px-2 py-2 text-sm" />
              <span className="text-muted-foreground">to</span>
              <input data-testid="onb-age-max" type="number" min={18} max={99} value={f.prefs.age_max} onChange={(e) => set("prefs", { ...f.prefs, age_max: +e.target.value })} className="w-20 rounded-xl border border-border bg-background px-2 py-2 text-sm" />
            </div>
          </Field>
          <label className="flex items-center gap-2 pt-7 text-sm" data-testid="onb-verified-only">
            <input type="checkbox" checked={f.prefs.verified_only} onChange={(e) => set("prefs", { ...f.prefs, verified_only: e.target.checked })} className="h-4 w-4 accent-rose-500" />
            Show me verified profiles only
          </label>
        </div>

        <label className="flex items-start gap-3 rounded-2xl bg-secondary/60 p-3.5" data-testid="onb-terms">
          <input type="checkbox" checked={f.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)} className="mt-0.5 h-5 w-5 accent-rose-500" />
          <span className="text-sm text-muted-foreground"><ShieldCheck size={14} weight="fill" className="mr-1 inline text-emerald-500" />I accept the Match terms & privacy. My exact location is never shown, and I can pause, hide or delete my Match profile anytime.</span>
        </label>

        <button data-testid="onb-activate" disabled={!canSubmit || activate.isPending} onClick={() => activate.mutate()}
          className="w-full rounded-full bg-rose-500 py-3 font-semibold text-white transition-transform hover:scale-[1.01] disabled:opacity-50">
          {activate.isPending ? "Activating…" : "Activate my Match profile"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <label className="text-sm font-medium">{label}</label>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
